/**
 * O próximo horário livre de um profissional.
 *
 * Substitui a "sugestão da IA" do novo agendamento, que era um texto fixo —
 * "Dra. Ana tem 15:00 livre na quinta" — aplicando 10/09 às 15:00 qualquer que
 * fosse a agenda. Não há IA nenhuma aqui: é a jornada da clínica menos o que já
 * está marcado, e por isso a tela não usa o visual de IA.
 *
 * Fica sem React e fora dos adaptadores: é regra, e regra se testa sem montar
 * tela.
 */

import { emMinutos } from './jornada';
import type { Agendamento, DiaDeFuncionamento } from './tipos';

export interface PedidoDeHorarioLivre {
    agendamentos: Agendamento[];
    horarios: DiaDeFuncionamento[];
    /** O nome do profissional, como a agenda o traz. */
    profissional: string;
    duracaoMin: number;
    /** `YYYY-MM-DD` do primeiro dia a procurar. */
    aPartirDe: string;
    /** Horário que já passou não é livre. */
    agora: Date;
    diasAFrente?: number;
    passoMin?: number;
}

export interface HorarioLivre {
    /** `YYYY-MM-DD`. */
    data: string;
    /** `HH:MM`. */
    hora: string;
}

const FUSO = 'America/Sao_Paulo';

function somarDias(dia: string, dias: number): string {
    const d = new Date(`${dia}T12:00:00-03:00`);
    d.setDate(d.getDate() + dias);
    return d.toLocaleDateString('en-CA', { timeZone: FUSO });
}

function diaDaSemana(dia: string): number {
    return new Date(`${dia}T12:00:00-03:00`).getDay();
}

function hhmm(minutos: number): string {
    return `${String(Math.floor(minutos / 60)).padStart(2, '0')}:${String(minutos % 60).padStart(2, '0')}`;
}

export function proximoHorarioLivre(p: PedidoDeHorarioLivre): HorarioLivre | null {
    const passo = p.passoMin ?? 30;
    const dias = p.diasAFrente ?? 14;
    if (!p.profissional || p.duracaoMin <= 0) return null;

    /* Cancelado e falta não seguram a cadeira. Bloqueio sem profissional é da
       clínica inteira (almoço, reforma); com profissional, só dele. */
    const ocupados = p.agendamentos
        .filter((a) => a.status !== 'cancelado' && a.status !== 'faltou')
        .filter((a) => (a.status === 'bloqueio' ? !a.profissional || a.profissional === p.profissional : a.profissional === p.profissional))
        .map((a) => {
            const inicio = new Date(a.inicioEm).getTime();
            return { inicio, fim: inicio + a.duracaoMin * 60_000 };
        });

    for (let i = 0; i < dias; i += 1) {
        const dia = somarDias(p.aPartirDe, i);
        const faixas = [...(p.horarios.find((d) => d.diaSemana === diaDaSemana(dia))?.faixas ?? [])]
            .sort((a, b) => emMinutos(a.inicio) - emMinutos(b.inicio));

        for (const faixa of faixas) {
            const fimDaFaixa = emMinutos(faixa.fim);
            /* O atendimento inteiro cabe na faixa: começar 11:30 uma consulta de
               uma hora numa manhã que fecha ao meio-dia invade o almoço. */
            for (let minuto = emMinutos(faixa.inicio); minuto + p.duracaoMin <= fimDaFaixa; minuto += passo) {
                const hora = hhmm(minuto);
                const inicio = new Date(`${dia}T${hora}:00-03:00`).getTime();
                const fim = inicio + p.duracaoMin * 60_000;
                if (inicio < p.agora.getTime()) continue;
                if (ocupados.some((o) => inicio < o.fim && fim > o.inicio)) continue;
                return { data: dia, hora };
            }
        }
    }
    return null;
}
